import React, { useState, useRef, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { logActivity } from '../services/api';
import './TankAI.css';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
}

const TankAI: React.FC = () => {
  const { user } = useAuth0();
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: 'Hello! I\'m Tank AI, your underground storage tank expert. I can help you with questions about UST regulations, environmental compliance, facility risk assessment, and data analysis. What would you like to know?',
      sender: 'ai',
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue.trim(),
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    // Log user interaction
    if (user) {
      logActivity('tank_ai_query', user, undefined, 0, 0);
    }

    try {
      // Simulate AI response (replace with actual API call)
      await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000));

      const aiResponse = generateAIResponse(userMessage.text);

      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: aiResponse,
        sender: 'ai',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: 'I apologize, but I\'m experiencing technical difficulties. Please try again later.',
        sender: 'ai',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const generateAIResponse = (userInput: string): string => {
    const input = userInput.toLowerCase().trim();

    // Handle greetings
    if (input.match(/^(hi|hello|hey|good morning|good afternoon|good evening|greetings)$/i) ||
        input.match(/^(hi|hello|hey)\s*[!.?]?$/i)) {
      return 'Hello! I\'m Tank AI, your expert assistant for underground storage tank information. I can help you with:\n\n• UST regulations and compliance\n• Risk assessment and scoring\n• Facility data and statistics\n• Leak detection and prevention\n• Environmental impact analysis\n\nWhat would you like to know about underground storage tanks today?';
    }

    // Handle help requests
    if (input.includes('help') || input.includes('what can you do') || input.includes('capabilities')) {
      return 'I can assist you with various aspects of underground storage tanks:\n\n📋 **Regulations**: EPA requirements, compliance standards, inspection protocols\n🔍 **Risk Assessment**: Understanding risk scores, factors affecting tank safety\n📊 **Data Analysis**: Facility statistics, leak rates, geographic trends\n🛡️ **Prevention**: Leak detection systems, corrosion protection, best practices\n🗺️ **Platform Features**: How to use search, maps, and data visualization tools\n\nJust ask me a question about any of these topics!';
    }

    // Handle thanks
    if (input.match(/^(thanks|thank you|thx|ty)$/i) || input.includes('thank you')) {
      return 'You\'re welcome! Is there anything else about underground storage tanks you\'d like to know? I\'m here to help with regulations, risk assessment, facility data, or any other UST-related questions.';
    }

    // Simple keyword-based responses (replace with actual AI integration)
    if (input.includes('regulation') || input.includes('epa') || input.includes('compliance') || input.includes('requirement')) {
      return 'Underground storage tank regulations are primarily governed by EPA\'s UST program under Subtitle I of RCRA. Key requirements include:\n\n• **Leak Detection**: Monthly monitoring for tanks and piping\n• **Corrosion Protection**: Required for metal tanks and piping\n• **Spill Prevention**: Catchment basins and overfill devices\n• **Financial Responsibility**: Insurance or other coverage for cleanup costs\n• **Operator Training**: Certified operators for each facility\n\nWould you like details about any specific regulatory requirement?';
    }

    if (input.includes('leak') || input.includes('release') || input.includes('contamination') || input.includes('spill')) {
      return 'UST releases can cause serious environmental contamination. Key information:\n\n**Common Causes**:\n• Corrosion (especially in older steel tanks)\n• Faulty installation or equipment failure\n• Spills during delivery\n• Physical damage\n\n**Our Data Shows**:\n• 325,288 total leak incidents recorded\n• 33.44% of facilities have experienced leaks\n• Early detection reduces cleanup costs by 60%\n\n**Prevention**: Regular monitoring, proper maintenance, and corrosion protection are essential. Would you like to know about specific prevention methods?';
    }

    if (input.includes('risk') || input.includes('assessment') || input.includes('score') || input.includes('rating')) {
      return 'Our risk assessment system evaluates facilities on a 1-10 scale:\n\n**Risk Factors**:\n• **Tank Age**: Older tanks (>30 years) have higher risk\n• **Construction**: Steel tanks without protection are high-risk\n• **Leak History**: Previous incidents increase risk score\n• **Location**: Proximity to water sources affects severity\n• **Compliance**: Violations indicate higher risk\n\n**Score Interpretation**:\n• 1-3: Low risk (newer, compliant facilities)\n• 4-6: Moderate risk (requires monitoring)\n• 7-10: High risk (immediate attention needed)\n\nWould you like to understand how to interpret specific risk scores?';
    }

    if (input.includes('age') || input.includes('old') || input.includes('replacement') || input.includes('year')) {
      return 'Tank age is a critical risk factor:\n\n**Key Statistics**:\n• Average tank age: 37.1 years\n• Pre-1988 steel tanks lack corrosion protection\n• Tanks >30 years old have 3x higher leak rates\n\n**Replacement Guidelines**:\n• Steel tanks without protection: Replace after 20-25 years\n• Protected steel tanks: 30-40 years typical lifespan\n• Fiberglass tanks: 30-50 years expected life\n\n**Risk by Age**:\n• 0-10 years: Low risk\n• 10-20 years: Monitor closely\n• 20-30 years: Consider replacement\n• 30+ years: High priority for replacement\n\nNeed information about replacement costs or procedures?';
    }

    if (input.includes('data') || input.includes('statistic') || input.includes('number') || input.includes('how many')) {
      return 'Here are the key statistics from our UST database:\n\n**Facility Data**:\n• Total facilities: 742,860\n• Total tanks: 2,280,075\n• Active tanks: 504,169 (22%)\n• Average tanks per facility: 3.1\n\n**Release Statistics**:\n• Total recorded leaks: 325,288\n• Facilities with leaks: 248,391 (33.44%)\n• Average cleanup cost: $125,000\n\n**Geographic Distribution**:\n• Highest concentration: Texas, California, Florida\n• Most releases: Industrial and urban areas\n• Highest risk regions: Older industrial corridors\n\nWould you like more detailed statistics for a specific state or metric?';
    }

    if (input.includes('search') || input.includes('find') || input.includes('locate') || input.includes('near')) {
      return 'Our Search feature helps you find UST facilities:\n\n**How to Search**:\n1. Go to the Search tab in the navigation\n2. Enter an address or location\n3. System will show nearby facilities with:\n   • Distance from search point\n   • Facility risk scores\n   • Tank counts and ages\n   • Leak history\n\n**Search Tips**:\n• Use complete addresses for best results\n• Can search by city, state, or ZIP\n• Results show facilities within 10 miles\n• Click any facility for detailed information\n\nWant to know how to interpret the search results?';
    }

    if (input.includes('map') || input.includes('visualization') || input.includes('geographic') || input.includes('state')) {
      return 'Our interactive maps provide powerful data visualization:\n\n**Map Features**:\n• **State View**: Aggregated statistics by state\n• **County View**: Detailed local-level data\n• **Heat Maps**: Color-coded by risk, density, or leak rates\n• **3D Globe**: Global perspective of UST distribution\n\n**How to Use**:\n• Click any region for detailed statistics\n• Use filters to focus on specific metrics\n• Toggle between different map styles\n• Zoom for more granular data\n\n**Color Coding**:\n• Green: Low risk/few facilities\n• Yellow: Moderate risk/density\n• Red: High risk/concentration\n\nNeed help with a specific map feature?';
    }

    // Handle yes/no responses
    if (input === 'yes' || input === 'no' || input === 'ok' || input === 'okay') {
      return 'I understand. What specific aspect of underground storage tanks would you like to explore? I can provide information about regulations, risk assessment, facility data, or help you use our platform features.';
    }

    // Handle very short or unclear inputs
    if (input.length < 3 || input.match(/^[a-z]{1,3}$/)) {
      return 'I didn\'t quite understand that. Could you please provide more details about what you\'d like to know? For example, you could ask about:\n• UST regulations\n• Risk assessment\n• Facility statistics\n• Leak prevention\n• How to use our search and map features';
    }

    // Default response for unmatched queries
    return 'I can help you with that topic as it relates to underground storage tanks. To provide the most relevant information, could you be more specific? I can assist with:\n\n• **Regulatory compliance** and EPA requirements\n• **Risk assessment** and facility scoring\n• **Data analysis** and statistics\n• **Environmental impact** and leak prevention\n• **Platform features** like search and mapping\n\nWhat aspect would you like to explore?';
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const suggestedQuestions = [
    "What are the main UST regulations?",
    "How is facility risk calculated?",
    "What causes tank leaks?",
    "How old are most tanks?",
    "Show me facility statistics"
  ];

  return (
    <div className="tank-ai-container">
      <div className="tank-ai-header">
        <h2>🤖 Tank AI Assistant</h2>
        <p>Your expert guide for underground storage tank data and regulations</p>
      </div>

      <div className="chat-container">
        <div className="messages-container">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`message ${message.sender === 'user' ? 'user-message' : 'ai-message'}`}
            >
              <div className="message-content">
                <div className="message-text">{message.text}</div>
                <div className="message-timestamp">
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="message ai-message">
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {messages.length === 1 && (
          <div className="suggested-questions">
            <h4>Try asking:</h4>
            <div className="suggestion-buttons">
              {suggestedQuestions.map((question, index) => (
                <button
                  key={index}
                  className="suggestion-button"
                  onClick={() => setInputValue(question)}
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="input-container">
          <div className="input-wrapper">
            <textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask me anything about underground storage tanks..."
              className="chat-input"
              rows={1}
              disabled={isLoading}
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading}
              className="send-button"
            >
              {isLoading ? '⏳' : '🚀'}
            </button>
          </div>
        </div>
      </div>

    </div>
  );
};

export default TankAI;